import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../../styles/theme';
import { Container } from '../common/Container';
import { Button } from '../common/Button';
import { DINOSAUR_ASCII } from '../../assets/dinosaur-ascii';
import { FossilFillsPreview } from '../features/FossilFillsPreview';

const HeroWrapper = styled.section`
  padding: ${theme.spacing['4xl']} 0;
  background: linear-gradient(135deg, ${theme.colors.backgroundSecondary} 0%, ${theme.colors.background} 100%);
  min-height: 600px;
  display: flex;
  align-items: center;
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing['3xl']};
  align-items: center;

  @media (min-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const HeroText = styled.div`
  text-align: center;

  @media (min-width: ${theme.breakpoints.lg}) {
    text-align: left;
  }
`;

const Title = styled(motion.h1)`
  font-size: ${theme.fontSizes['3xl']};
  margin-bottom: ${theme.spacing.lg};
  
  @media (min-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes['5xl']};
  }

  span {
    color: ${theme.colors.primary};
  }
`;

const Subtitle = styled(motion.p)`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.secondary};
  line-height: 1.8;
  margin-bottom: ${theme.spacing.xl};
  
  @media (min-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes.xl};
  }
`;

const Actions = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  
  @media (min-width: ${theme.breakpoints.sm}) {
    flex-direction: row;
    justify-content: center;
  }

  @media (min-width: ${theme.breakpoints.lg}) {
    justify-content: flex-start;
  }
`;

const DemoArea = styled(motion.div)`
  background-color: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.lg};
  position: relative;
  overflow: hidden;
`;


const DinosaurDecoration = styled.pre`
  position: absolute;
  bottom: -20px;
  right: -20px;
  font-family: ${theme.fonts.code};
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.primaryLight};
  opacity: 0.2;
  user-select: none;
  transform: rotate(-10deg);
`;

const FeatureList = styled.div`
  margin-top: ${theme.spacing.lg};
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  justify-content: center;
  
  @media (min-width: ${theme.breakpoints.lg}) {
    justify-content: flex-start;
  }
`;

const Feature = styled.span`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};

  &::before {
    content: '✓';
    color: ${theme.colors.success};
    font-weight: bold;
  }
`;

export const Hero: React.FC = () => {
  return (
    <HeroWrapper>
      <Container>
        <HeroContent>
          <HeroText>
            <Title
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Make Your Prompts <span>Roar</span>!
            </Title>
            <Subtitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Transform basic prompts into powerful, context-rich instructions 
              that get you better results from ChatGPT, Claude, and other AI assistants.
            </Subtitle>
            <Actions
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Button as={Link} to="/fossil_fills" size="large">
                Try Fossil Fills Demo
              </Button>
              <Button as={Link} to="/extension" variant="secondary" size="large">
                Get Browser Extension
              </Button>
            </Actions>
            <FeatureList>
              <Feature>Free to use</Feature>
              <Feature>Works with ChatGPT & Claude</Feature>
              <Feature>Privacy-focused</Feature>
              <Feature>No account required</Feature>
            </FeatureList>
          </HeroText>
          
          <DemoArea
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <FossilFillsPreview />
            <DinosaurDecoration>{DINOSAUR_ASCII}</DinosaurDecoration>
          </DemoArea>
        </HeroContent>
      </Container>
    </HeroWrapper>
  );
};